
import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import ReCAPTCHA from "react-google-recaptcha"; // New import for reCAPTCHA
import bannerimg from "../../assets/contact/bacground.png";
import integrationProcessImage from "../../assets/contact/integration_background.png";
import worldMapBackground from "../../assets/contact/form_background.png";
import { Mail, MapPin, Phone, X } from "lucide-react";

Modal.setAppElement("#root");

const MemoizedRecaptcha = React.memo(({ recaptchaRef, errors }) => (
  <div>
    <ReCAPTCHA
      ref={recaptchaRef}
      sitekey="6LdZ5xYsAAAAAHzRZXSHBOdhMYqtlAf-a0U77wXE"
      size="normal"
    />
    {errors?.recaptcha && (
      <p className="text-red-400 mt-2">{errors.recaptcha}</p>
    )}
  </div>
));


const Contact = () => {
  const [categories, setCategories] = useState([]);


  // --- FORM STATES ---
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    apiService: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  // --- reCAPTCHA ---
  const recaptchaRef = useRef(); // Ref for reCAPTCHA

  // --- SUCCESS MODAL ---
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch Categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch("https://admin.apigalaxy.in/fe/category_list");
        const json = await res.json();
        if (json.success) setCategories(json.data);
      } catch (error) {
        console.error("Category load error:", error);
      }
    };
    fetchCategories();
  }, []);

  // Handle Input
 const handleChange = (e) => {
  console.log(`[handleChange] Input name: ${e.target.name}, value: ${e.target.value}`);
  setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
};





  // Validation
  const validateForm = () => {
    let newErrors = {};

    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.(com|in|net|org)$/i.test(form.email)) {
      newErrors.email = "Enter a valid email";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(form.phone)) {
      newErrors.phone = "Phone must be 10 digits";
    }

    if (!form.apiService) newErrors.apiService = "Please select a service";

    if (!form.message.trim()) newErrors.message = "Message is required";

    // reCAPTCHA validation
    const token = recaptchaRef.current?.getValue();
    if (!token) newErrors.recaptcha = "Please complete the reCAPTCHA verification";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Get reCAPTCHA token
    const token = recaptchaRef.current.getValue();
    if (!token) {
      setErrors({ ...errors, recaptcha: "Please complete the reCAPTCHA verification" });
      return;
    }

    const payload = {
      name: form.name,
      email: form.email,
      contactNo: form.phone,
      companyName: form.companyName,
      apiCategory: [form.apiService],
      message: form.message,
      remark: "",
      recaptchaToken: "", 
    };

    try {
      const res = await fetch("https://admin.apigalaxy.in/fe/save_contact_form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      console.log("Submitted:", json);

      setModalOpen(true);

      // Reset form
      setForm({
        name: "",
        email: "",
        phone: "",
        companyName: "",
        apiService: "",
        message: "",
      });

      // Reset reCAPTCHA
      recaptchaRef.current.reset();
      setErrors({}); // Clear errors
    } catch (error) {
      console.error("Submit Error:", error);
      alert("Something went wrong.");
    }
  };


  // UI COMPONENTS
  // Moved outside Contact component for stable reference and to avoid remount issues


  // CONTACT CARD COMPONENT
  const ContactInfoCard = ({ icon: Icon, title, content }) => (
    <div className="bg-white rounded-lg p-6 flex items-start gap-4 border border-gray-200 mb-6 hover:border-[#2F56A6] hover:shadow-lg">
      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
        <Icon size={20} />
      </div>
      <div>
        <h4 className="font-semibold text-lg">{title}</h4>
        <p className="text-gray-600">{content}</p>
      </div>
    </div>
  );


  // INPUT COMPONENT
const FormInput = ({ type = "text", placeholder, name, isTextArea = false, value, onChange }) =>
  !isTextArea ? (
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      autoComplete="off"
      className="w-full p-4 text-white bg-transparent border border-[#AFACAC] rounded-[3px] 
                focus:outline-none focus:border-white focus:ring-2 focus:ring-[#00AFEF]"
      placeholder={placeholder}
    />
  ) : (
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      rows="4"
      autoComplete="off"
      className="w-full p-4 text-white bg-transparent border border-[#AFACAC] rounded-[3px] 
                focus:outline-none focus:border-white focus:ring-2 focus:ring-[#00AFEF]"
      placeholder={placeholder}
    />
  );

// DROPDOWN COMPONENT
const FormDropdown = ({ categories, value, onChange }) => (
  <select
    name="apiService"
    value={value}
    onChange={onChange}
    className="w-full p-4 text-white bg-transparent border border-[#AFACAC] rounded-[3px]
               focus:outline-none focus:border-white focus:ring-2 focus:ring-[#00AFEF]"
  >
    <option value="" hidden>
      Interested API Services
    </option>

    {categories.map((item) => (
      <option key={item._id} value={item.service_type_name} className="bg-[#162758]">
        {item.service_type_name}
      </option>
    ))}
  </select>
);


  return (
    <>
      {/* BANNER */}
      <div className="relative w-full h-[300px] md:h-auto">
        <img src={bannerimg} alt="Banner" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
          <h1 className="text-white text-5xl font-goldman">Contact Us</h1>
          <p className="text-[#D9D9D9] text-center">
            We’d love to hear from you!
          </p>
        </div>
      </div>

      {/* CONTACT SECTION */}
      <section
        className="py-24"
        style={{ backgroundImage: `url(${integrationProcessImage})`, backgroundSize: "contain" }}
      >
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* LEFT CARD */}
          <div className="pt-20">
            <ContactInfoCard icon={Mail} title="Email" content="support@apigalaxy.com" />
            <ContactInfoCard
              icon={MapPin}
              title="Address"
              content="69, near Apollo Pharmacy, Dhadiwal Layout, Nagpur"
            />
            <ContactInfoCard
              icon={Phone}
              title="Contact"
              content={<><div>+91-8600555760</div><div>+91-8600555750</div></>}
            />
          </div>

          {/* RIGHT FORM */}
          <div className="pt-10 pb-10 pr-10 pl-10 rounded-lg lg:col-span-2  bg-cover bg-center"
            style={{ backgroundImage: `url(${worldMapBackground})`}}
          >
            <h2 className="text-white text-4xl mb-3 mt-20">Drop Your Message</h2>
            <p className="text-white/80 text-lg mb-5">Please fill out the form and provide details of your request, we will reply back.</p>
            <form className="space-y-8" onSubmit={handleSubmit}>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormInput placeholder="Name" name="name" />
                <FormInput type="email" placeholder="Email" name="email" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormInput type="tel" placeholder="Phone" name="phone" />
                <FormInput placeholder="Company Name" name="companyName" />
              </div>

            <FormDropdown categories={categories} value={form.apiService} onChange={handleChange} />
            <FormInput placeholder="Message..." name="message" isTextArea value={form.message} onChange={handleChange} />

              {/* reCAPTCHA */}
              <div>
                <MemoizedRecaptcha recaptchaRef={recaptchaRef} errors={errors} />
              </div>

              <button
                type="submit"
                className="w-full py-4 text-xl font-semibold text-white rounded-lg"
                style={{ background: "linear-gradient(90deg, #00AFEF 0%, #060438 100%)" }}
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* SUCCESS MODAL */}
      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        className="bg-white p-8 rounded-xl shadow-xl max-w-md mx-auto mt-40"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-green-600">Success!</h2>
          <button onClick={() => setModalOpen(false)}> <X /> </button>
        </div>

        <p className="text-gray-700">
          Your message has been submitted successfully. Our team will contact you shortly.
        </p>

        <div className="mt-6 text-right">
          <button
            onClick={() => setModalOpen(false)}
            className="px-6 py-2 bg-green-600 text-white rounded-lg"
          >
            OK
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Contact;